import { useEffect, useState } from 'react';

/**
 * My List: movies and shows a profile saved to watch later, from the library or from Discover. Kept on the server per
 * profile (/api/mylist), shared by every screen through one small store so a change shows everywhere at once.
 */
export function myListKey(it) {
  if (!it) return '';
  if (it.source === 'jellyfin' && it.id != null) return `jf:${String(it.id).replace(/[^a-f0-9-]/gi, '')}`;
  if (it.tmdbId) return `tmdb:${it.type === 'movie' ? 'movie' : 'show'}:${Number(it.tmdbId) || ''}`;
  return '';
}

/** Only whole titles: a movie or a show, not an episode, song or book. */
export function myListEligible(it) {
  return !!myListKey(it) && (it.type === 'movie' || it.type === 'show' || it.type === 'tv' || it.type === 'series');
}

const store = { items: null, loading: null, subs: new Set() };
const emit = () => store.subs.forEach((f) => f(store.items || []));

function load(force = false) {
  if (store.loading && !force) return store.loading;
  store.loading = fetch('/api/mylist', { credentials: 'include' })
    .then((r) => (r.ok ? r.json() : { items: [] }))
    .then((j) => {
      store.items = Array.isArray(j.items) ? j.items : [];
      emit();
    })
    .catch(() => {})
    .finally(() => {
      store.loading = null;
    });
  return store.loading;
}

/** A profile switch brings another list. */
export function reloadMyList() {
  store.items = null;
  return load(true);
}

export async function setInMyList(item, on) {
  const key = myListKey(item);
  if (!key) return false;
  const before = store.items || [];
  /** Show the change straight away; the server's answer replaces it. */
  store.items = on ? [{ ...item, key, addedAt: Date.now() }, ...before.filter((x) => x.key !== key)] : before.filter((x) => x.key !== key);
  emit();
  try {
    const r = await fetch('/api/mylist', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ item, on }),
    });
    const j = await r.json();
    if (!r.ok) throw new Error(j.error || 'failed');
    store.items = j.items || [];
    emit();
    return true;
  } catch (_) {
    store.items = before;
    emit();
    return false;
  }
}

export function useMyList() {
  const [items, setItems] = useState(store.items || []);
  useEffect(() => {
    store.subs.add(setItems);
    if (store.items == null) load();
    else setItems(store.items);
    return () => {
      store.subs.delete(setItems);
    };
  }, []);
  return items;
}
